import { toObservable } from '@angular/core/rxjs-interop';

import { NetworkStatusService } from 'app/network-status.service';
import { on } from '@ngrx/store';
import { ChangeDetectorRef, Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { NavigationItem, NavigationService } from 'app/navigation.service';
import { ButtonModule } from 'primeng/button';
import { BehaviorSubject, combineLatest, filter, fromEvent, map, share, shareReplay, throttle, throttleTime } from 'rxjs';
import { Router, ActivatedRoute, RouterOutlet, ActivatedRouteSnapshot, NavigationEnd, RouterModule } from '@angular/router';
import * as routerState from 'app/reducers/router.reducer';
import { select, Store } from '@ngrx/store';
import { BandService } from 'app/core/band/services/band.service';
import { getAvatarLabelFromName, getAvatarStyleFromName } from '@shared/helpers/avatar.helpers';
import { User } from 'app/core/api/types';
import { MediaQueryService } from './media-query.service';
import { AuthService } from 'app/core/auth/auth.service';
import { ToolbarService } from './toolbar.service';
import { SettingsService } from 'app/settings.service';
import { routerTransition } from 'app/animations/router-transition';
import { ThemeService } from 'app/core/settings/theme.service';
import { DevicePermissionService } from './device-permissions.service';
import { DialogService } from 'primeng/dynamicdialog';
import { DevicePermissionDialogComponent } from './components/device-permission-dialog/device-permission-dialog.component';
import { AppWrapperService } from 'app/app-wrapper.service';
import { bounceInDownOnEnterAnimation, fadeInDownAnimation, fadeInDownOnEnterAnimation, fadeInOnEnterAnimation, fadeInUpOnEnterAnimation, pulseAnimation } from 'app/animations';
import { SwipeNavigationService } from 'app/swipe-navigation.service';
import { Haptics, ImpactStyle } from '@capacitor/haptics';
import { HapticsService } from 'haptics.serivce';
import { animate, keyframes, style, transition, trigger } from '@angular/animations';
import { OutboxService } from 'app/core/db/outbox-count.service';
import { CommonModule } from '@angular/common';
import { BadgeModule } from 'primeng/badge';
import { OverlayModule } from 'primeng/overlay';
import { MenuModule } from 'primeng/menu';
import { AppCardioGramComponent } from '@shared/components/cardiogram/cardiogram.component';
import { MemberIdCardComponent } from '@shared/components/member-id-card/member-id-card.component';
import { BandIdCardComponent } from '@shared/components/band-id-card/band-id-card.component';
import { OverlayBadgeComponent } from '@shared/components/overlay-badge/overlay-badge.component';
import { AppToolbarComponent } from '@shared/components/toolbar/toolbar.component';
import { ToastModule } from 'primeng/toast';
import { PopoverModule } from 'primeng/popover';
import { TranslateModule } from '@ngx-translate/core';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { VoiceAssistantService } from './assistant/voice-assistant.service';
import { AssistantNluService } from './assistant/assistant-nlu.service';
import { DeskAppointmentService } from 'app/core/desk/desk-appointment.service';
import { VoiceVisualService } from './assistant/voice-visualization.service';
import { VoiceGlowService } from './assistant/voice-glow.service';




@Component({
    selector: 'app-wrapper',
    templateUrl: './app-wrapper.component.html',
    styleUrls: ['./app-wrapper.component.scss'],
    animations: [
        routerTransition,
        fadeInOnEnterAnimation(),
        trigger('pulseOnChange', [
            transition(':increment', [
                animate('240ms ease-out', keyframes([
                    style({ transform: 'scale(1)', offset: 0 }),
                    style({ transform: 'scale(1.22)', offset: 0.4 }),
                    style({ transform: 'scale(1)', offset: 1 })
                ]))
            ]),

        ]),
    ],
    imports: [
        ButtonModule,
        CommonModule,
        BadgeModule,
        OverlayModule,
        MenuModule,
        AppCardioGramComponent,
        MemberIdCardComponent,
        BandIdCardComponent,
        OverlayBadgeComponent,
        AppToolbarComponent,
        ToastModule,
        RouterModule,
        PopoverModule,
        TranslateModule,
        ConfirmDialogModule
    ],
})


export class AppWrapperComponent implements OnInit {

    @ViewChild('overlay') overlay!: any;
    @ViewChild('main', { static: true }) mainArea!: ElementRef;
    @ViewChild('networkStatusPopover') networkStatusPopover!: ElementRef;

    showBandMembersOverlay = false;



    // openOverlay(event: MouseEvent): void {
    //     if (!this.showBandMembersOverlay) {
    //         this.showBandMembersOverlay = true;
    //         this.overlay.toggle(event); // Open or close the overlay
    //     }
    // }

    activeRoute$ = this.store.pipe(select(routerState.getActiveRoute));
    activeRouterState$ = this.store.pipe(select(routerState.getActiveRouteParams));

    mainMenuItems$ = this.navigationService.getMainNavigationItems().pipe(
        shareReplay(1) // optional: verhindert mehrfachen Build
    );
    activeTopMenuItem$ = new BehaviorSubject<NavigationItem | null>(null);
    activeChildMenuItem$ = new BehaviorSubject<NavigationItem | null>(null);
    activeInfantMenuItem$ = new BehaviorSubject<NavigationItem | null>(null);
    infantMenuVisible$ = new BehaviorSubject<boolean>(false);

    hideChildren$ = this.store.pipe(select(routerState.getIsOnBandSelectionPage)).pipe(
        map(isOnBandSelectionPage => isOnBandSelectionPage)
    );


    constructor(
        public navigationService: NavigationService,
        private router: Router,
        private store: Store,
        public route: ActivatedRoute,
        public bandService: BandService,
        public mediaQuery: MediaQueryService,
        public authService: AuthService,
        private cdr: ChangeDetectorRef,
        public toolbarService: ToolbarService,
        private settingsService: SettingsService,
        public themeService: ThemeService,
        private devicePermissionService: DevicePermissionService,
        private dialogService: DialogService,
        public appWrapperService: AppWrapperService,
        private swipeNavigationService: SwipeNavigationService,
        public networkStatusService: NetworkStatusService,
        private hapticsService: HapticsService,
        public outboxService: OutboxService,
        private voiceAssistantService: VoiceAssistantService,
        private assistantNLUService: AssistantNluService,
        public voiceVisual: VoiceVisualService,
        private glow: VoiceGlowService

    ) {
        this.assistantNLUService.start();
        this.glow.start();
        this.router.events
            .pipe(filter(event => event instanceof NavigationEnd))
            .subscribe(() => {
                const tree = this.router.routerState.snapshot.root;
                const deepest = this.getDeepestChild(tree);
                const state = deepest.data?.['animation'] ?? null;
                this.appWrapperService.updateAnimationState(state);
            });

    }
    private getDeepestChild(route: ActivatedRouteSnapshot): ActivatedRouteSnapshot {
        while (route.firstChild) route = route.firstChild;
        return route;
    }

    ngOnInit(): void {
        this.setActiveMenuItem();
        this.setActiveChildMenuItem();
        this.setActiveInfantMenuItem();
        this.bandService.loadBands();
        if (!this.devicePermissionService.hasPermissionsGranted()) {
            this.showDevicePermissionDialog();
        }
        this.settingsService.settings$.subscribe(settings => {
            if (settings?.showChangelog && this.devicePermissionService.hasPermissionsGranted()) {
                this.settingsService.showChangeLog().onClose.subscribe(() => {
                    this.settingsService.saveChangelogVersion();
                    this.settingsService.updateSettings({ showChangelog: false });
                });
            }
        });
        // this.registerFullScreenListenerOnScroll();
    }
    private lastScrollTop: number = 0;

    ngAfterViewInit(): void {
        this.swipeNavigationService.registerSwipeArea(this.mainArea.nativeElement);
        this.appWrapperService.registerMainArea(this.mainArea.nativeElement);
    }

    private registerFullScreenListenerOnScroll() {
        const el = this.mainArea.nativeElement;
        const headerHeight = this.getHeaderHeight();
        let lastState: boolean | null = null;

        fromEvent(el, 'scroll')
            .pipe(throttleTime(100))
            .subscribe(() => {
                const scrollTop = el.scrollTop;

                const isAtTop = scrollTop <= headerHeight;

                if (!isAtTop && lastState !== true) {
                    this.rememberScrollRatio(el);
                    this.appWrapperService.displayContentFullScreen.set(true);
                    lastState = true;
                }

                if (isAtTop && lastState !== false) {
                    this.rememberScrollRatio(el);
                    this.appWrapperService.displayContentFullScreen.set(false);
                    lastState = false;
                }
            });


        toObservable(this.appWrapperService.displayContentFullScreen)
            .subscribe(() => {
                setTimeout(() => {
                    this.restoreScrollRatio(el);
                }, 10);
            });
    }

    private getHeaderHeight(): number {
        const header = document.querySelector('.app-header');
        return header ? header.clientHeight : 0;
    }



    private savedScrollRatio = 0;

    private rememberScrollRatio(el: HTMLElement) {
        const scrollTop = el.scrollTop;
        const maxScroll = el.scrollHeight - el.clientHeight;
        this.savedScrollRatio = maxScroll > 0 ? scrollTop / maxScroll : 0;
    }

    private restoreScrollRatio(el: HTMLElement) {
        const maxScroll = el.scrollHeight - el.clientHeight;
        el.scrollTop = maxScroll * this.savedScrollRatio;
    }


    private showDevicePermissionDialog() {
        const ref = this.dialogService.open(DevicePermissionDialogComponent, {
            closable: false,
            header: 'Permissions Required',
            width: '100vw',
            height: '100vh',
            data: {},
        });

        ref.onClose.subscribe(() => {
            this.devicePermissionService.markPermissionsGranted();
        });
    }

    cdrCheck(event: any): void {
        this.cdr.markForCheck();
        this.cdr.detectChanges();
    }

    setActiveMenuItem(): void {
        combineLatest([
            this.mainMenuItems$,
            this.activeRoute$
        ])
            .subscribe(([menuItems, activeRoute]) => {

                const activeItem = menuItems.find(item =>
                    activeRoute.includes(item.routerLink) // Match the parent route
                );

                if (activeItem) {
                    this.activeTopMenuItem$.next(activeItem);
                }
            });
    }

    setActiveChildMenuItem(): void {
        combineLatest([
            this.activeTopMenuItem$,
            this.activeRoute$
        ])
            .subscribe(([activeTopMenuItem, activeRoute]) => {
                if (activeTopMenuItem) {
                    const activeChildItem = activeTopMenuItem.children?.find(item =>
                        activeRoute.includes(item.routerLink) // Match the child route
                    );

                    if (activeChildItem) {
                        this.activeChildMenuItem$.next(activeChildItem);
                    }
                }
            });
    }

    setActiveInfantMenuItem(): void {
        combineLatest([
            this.activeChildMenuItem$,
            this.activeRoute$
        ])
            .subscribe(([activeChildMenuItem, activeRoute]) => {
                if (activeChildMenuItem) {
                    const activeInfantItem = activeChildMenuItem.children?.find(item =>
                        activeRoute.includes(item.routerLink) // Match the child route
                    );

                    if (activeInfantItem) {
                        this.activeInfantMenuItem$.next(activeInfantItem);
                    }
                }
            });
    }

    clickEvent(event: MouseEvent): void {

    }

    selectTopMenuItem(item: any): void {
        this.cdr.markForCheck();
        this.activeTopMenuItem$.next(item);
        this.router.navigate([item.routerLink], { relativeTo: this.route });
    }

    navigateHome() {
        this.router.navigate(['/app/wrapper/desk/dashboard']);
    }

    isActiveTopMenuItem(item: any): boolean {
        return this.activeTopMenuItem$.value?.routerLink === item.routerLink;
    }

    selectChildMenuItem(item: any): void {
        this.activeChildMenuItem$.next(item);
        this.router.navigate([item.routerLink], { relativeTo: this.route });
        // if (!this.isActiveChildMenuItem(item)) {
        //     this.activeChildMenuItem$.next(item);
        //     this.router.navigate([item.routerLink], { relativeTo: this.route });
        //     this.infantMenuVisible$.next(true);
        // } else {
        //     this.infantMenuVisible$.next(!this.infantMenuVisible$.value);
        // }
    }
    isActiveChildMenuItem(item: any): boolean {
        return this.activeChildMenuItem$.value?.routerLink === item.routerLink;
    }

    selectInfantMenuItem(item: any): void {
        this.activeInfantMenuItem$.next(item);
    }

    isSelectedInfantMenuItem(item: any): boolean {
        return this.activeInfantMenuItem$.value === item;
    }



    getAvatarLabel(value: User): string {
        const fullName = `${value.firstName} ${value.lastName}`;
        return getAvatarLabelFromName(fullName);
    }

    getAvatarStyle(value: User): any {
        const fullName = `${value.firstName} ${value.lastName}`;
        return getAvatarStyleFromName(fullName, false)
    }

    navigateToSettings(): void {
        this.router.navigate(['/app/wrapper/settings']);
    }

    prepareRoute(outlet: RouterOutlet) {
        return outlet?.activatedRouteData?.['animation'] || null;
    }

    getLogoStyle(): any {
        return this.themeService.isDarkMode() ? {
            'fill': 'var(--p-primary-color)'
        } : {

            'fill': 'var(--p-surface-100)'
        }
    }
    networkPopoverOpen = false;
    heartbeatInterval: any;

    onNetworkPopoverShown() {
        this.networkPopoverOpen = true;
        this.startHeartbeatHaptics();
    }

    onNetworkPopoverHidden() {
        this.networkPopoverOpen = false;
        clearInterval(this.heartbeatInterval);
    }

    startHeartbeatHaptics() {
        if (!this.networkPopoverOpen) return;

        if (this.networkStatusService.networkIssuesSubject.value) {
            setTimeout(() => {
                this.getDeadImpactHaptics();
                this.heartbeatInterval = setInterval(() => {
                    this.hapticsService.impact(ImpactStyle.Light);
                }, 3000);
            }, 750);
        } else {
            setTimeout(() => {
                this.getAliveHeartbeatHaptics();
                this.heartbeatInterval = setInterval(() => {
                    this.getAliveHeartbeatHaptics();
                }, 3000);
            }, 750);
        }
    }

    private getAliveHeartbeatHaptics() {
        this.hapticsService.impact(ImpactStyle.Heavy);
        setTimeout(() => {
            this.hapticsService.impact(ImpactStyle.Light);
        }, 200);
    }

    private getDeadImpactHaptics() {

    }

    activateAIAssistant() {
        this.navigateHome();
        // this.voiceAssistantService.toggle({ lang: 'de-DE' })
        // this.voiceAssistantService.state$().subscribe(state => {
        //     console.log('Voice Assistant State:', state);
        // })
        // this.voiceAssistantService.partial$().subscribe(partial => {
        //     console.log('Voice Assistant Partial:', partial);
        // })
        // this.voiceAssistantService.final$().subscribe(command => {
        //     console.log('Voice Assistant Final:', command);
        // })
    }
}
